import React, { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Trash2, RefreshCw, TrendingUp, TrendingDown, Wallet, Sparkles, Calculator, Star, Check, Loader2 } from "lucide-react";
import api, { fmtEur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const monthLabel = (ym) => { if (!ym) return "—"; const [y, m] = ym.split("-"); return `${MESI[+m - 1] || m} ${y}`; };
const nextMonth = () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const num = (v) => Number(v || 0);
const sum = (arr) => (arr || []).reduce((s, r) => s + num(r.importo), 0);

const EMPTY = { ricavi: [], spese: [], ricavi_extra: [], spese_extra: [] };

function monthOptions() {
  const y = new Date().getFullYear();
  const out = [];
  for (let yr = y - 1; yr <= y + 1; yr++) for (let i = 0; i < 12; i++) out.push(`${yr}-${String(i + 1).padStart(2, "0")}`);
  return out;
}

export default function Previsioni() {
  const { can, selectedStructure, structures = [] } = useAuth();
  const [month, setMonth] = useState(nextMonth());
  const [sheet, setSheet] = useState(EMPTY);
  const [fonte, setFonte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("saved");
  const savedSnapshotRef = useRef("");
  const loadedKeyRef = useRef("");
  const saveTimerRef = useRef(null);
  const structLabel = selectedStructure === "all" ? "Tutte le strutture" : (structures.find((s) => s.id === selectedStructure)?.nome || "");

  const load = useCallback(async () => {
    setLoading(true);
    let next = EMPTY;
    try {
      const { data } = await api.get("/previsioni", { params: { mese: month, structure_id: selectedStructure } });
      if (data) {
        next = { ricavi: data.ricavi || [], spese: data.spese || [], ricavi_extra: data.ricavi_extra || [], spese_extra: data.spese_extra || [] };
        setFonte(null);
      } else {
        const pf = (await api.get("/previsioni/prefill", { params: { mese: month, structure_id: selectedStructure } })).data;
        next = { ricavi: pf.ricavi || [], spese: pf.spese || [], ricavi_extra: pf.ricavi_extra || [], spese_extra: pf.spese_extra || [] };
        setFonte(pf.fonte);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
      next = EMPTY;
    } finally {
      setSheet(next);
      savedSnapshotRef.current = JSON.stringify(next);
      loadedKeyRef.current = `${month}|${selectedStructure}`;
      setSaveStatus("saved");
      setLoading(false);
    }
  }, [month, selectedStructure]);

  useEffect(() => { load(); }, [load]);

  const persist = useCallback(async (m, sid, data, snap) => {
    setSaveStatus("saving");
    try {
      await api.post("/previsioni", { mese: m, structure_id: sid, ...data });
      savedSnapshotRef.current = snap;
      setFonte(null);
      setSaveStatus("saved");
    } catch (e) { setSaveStatus("error"); toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  }, []);

  // Salvataggio automatico (debounce): niente pulsante Salva
  useEffect(() => {
    if (loading) return;
    if (loadedKeyRef.current !== `${month}|${selectedStructure}`) return; // sheet non ancora caricato per questo mese
    const snap = JSON.stringify(sheet);
    if (snap === savedSnapshotRef.current) return; // nessuna modifica
    const m = month, sid = selectedStructure, data = sheet;
    clearTimeout(saveTimerRef.current);
    setSaveStatus("saving");
    saveTimerRef.current = setTimeout(() => persist(m, sid, data, snap), 800);
  }, [sheet, loading, month, selectedStructure, persist]);

  const reloadFromReal = async () => {
    try {
      const pf = (await api.get("/previsioni/prefill", { params: { mese: month, structure_id: selectedStructure } })).data;
      setSheet((s) => ({ ...s, ricavi: pf.ricavi || [], spese: pf.spese || [] }));
      setFonte(pf.fonte);
      toast.success(`Voci ricalcolate dai dati reali di ${monthLabel(pf.fonte)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore ricalcolo"); }
  };

  const upd = (key, idx, field, value) => setSheet((s) => ({ ...s, [key]: s[key].map((r, i) => i === idx ? { ...r, [field]: value } : r) }));
  const add = (key, row) => setSheet((s) => ({ ...s, [key]: [...s[key], row] }));
  const del = (key, idx) => setSheet((s) => ({ ...s, [key]: s[key].filter((_, i) => i !== idx) }));

  if (!can("controllo")) return <div className="gr-card p-10 text-center text-muted-foreground">Non hai i permessi per questa sezione.</div>;

  const totRicavi = sum(sheet.ricavi);
  const totSpese = sum(sheet.spese);
  const utile = totRicavi - totSpese;
  const totRicaviExtra = sum(sheet.ricavi_extra);
  const totSpeseExtra = sum(sheet.spese_extra);
  const risultatoExtra = totRicaviExtra - totSpeseExtra;
  const risultatoFinale = utile + risultatoExtra;

  return (
    <div className="pb-20" data-testid="previsioni-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Calculator size={22} className="text-primary" /> Previsioni</h1>
          <p className="text-sm text-muted-foreground">Business plan del mese · {structLabel} — compila ricavi e spese e ottieni l'utile previsto</p>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><Star size={13} className="fill-amber-400 text-amber-500" /> Tocca la stellina per marcare una voce come <b>ricorrente</b>: verrà riproposta in automatico nei mesi successivi.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-11 w-44 font-semibold" data-testid="prev-month-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {monthOptions().map((k) => <SelectItem key={k} value={k} data-testid={`prev-month-${k}`}>{monthLabel(k)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={reloadFromReal} data-testid="prev-reload-btn"><RefreshCw size={15} className="mr-1" /> Dati reali</Button>
          <div className="inline-flex items-center gap-1.5 h-11 px-3 rounded-md text-sm font-semibold" data-testid="prev-save-status" data-status={saveStatus}
            style={{ background: saveStatus === "error" ? "#fef2f2" : "#f0fdf4", color: saveStatus === "error" ? "#dc2626" : saveStatus === "saving" ? "#c2660c" : "#15803d" }}>
            {saveStatus === "saving" ? <><Loader2 size={15} className="animate-spin" /> Salvataggio…</>
              : saveStatus === "error" ? <>Errore — riprova</>
              : <><Check size={15} /> Salvato</>}
          </div>
        </div>
      </div>

      {fonte && (
        <div className="mb-4 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm" data-testid="prev-fonte-banner">
          Voci precompilate automaticamente dai dati reali di <b>{monthLabel(fonte)}</b>. Modificale liberamente: le modifiche vengono salvate in automatico.
        </div>
      )}

      {loading ? <div className="gr-card p-10 text-center text-muted-foreground">Caricamento...</div> : (
      <div className="grid lg:grid-cols-2 gap-5">
        {/* RICAVI */}
        <div className="gr-card p-5" data-testid="prev-ricavi-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-head font-bold flex items-center gap-2 text-emerald-700"><TrendingUp size={18} /> Ricavi</div>
            <Button size="sm" variant="outline" onClick={() => add("ricavi", { desc: "", importo: "" })} data-testid="prev-add-ricavo"><Plus size={14} className="mr-1" /> Riga</Button>
          </div>
          <RowsEditor list={sheet.ricavi} listKey="ricavi" upd={upd} del={del} placeholder="Descrizione ricavo (es. Rette, Contributi)" testid="ricavo" />
          <TotalRow label="Totale ricavi" value={totRicavi} color="text-emerald-700" testid="tot-ricavi" />
        </div>

        {/* SPESE */}
        <div className="gr-card p-5" data-testid="prev-spese-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-head font-bold flex items-center gap-2 text-rose-700"><TrendingDown size={18} /> Spese</div>
            <Button size="sm" variant="outline" onClick={() => add("spese", { categoria: "custom", label: "", importo: "", fisso: false })} data-testid="prev-add-spesa"><Plus size={14} className="mr-1" /> Riga</Button>
          </div>
          <div className="space-y-2">
            {sheet.spese.map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`prev-spesa-${i}`}>
                {r.fisso ? (
                  <div className="flex-1 text-sm font-semibold px-3 py-2 rounded-md bg-muted/60" data-testid={`prev-spesa-label-${i}`}>{r.label}</div>
                ) : (
                  <Input value={r.label || ""} onChange={(e) => upd("spese", i, "label", e.target.value)} placeholder="Voce di spesa (es. Manutenzione)" className="flex-1" data-testid={`prev-spesa-label-input-${i}`} />
                )}
                <Input type="text" inputMode="decimal" value={r.importo} onChange={(e) => upd("spese", i, "importo", e.target.value.replace(/[^\d.,]/g, "").replace(",", "."))} placeholder="0" className="w-32 text-right tabnum" data-testid={`prev-spesa-importo-${i}`} />
                {!r.fisso ? (
                  <>
                    <button onClick={() => upd("spese", i, "ricorrente", !r.ricorrente)} title={r.ricorrente ? "Ricorrente: verrà riproposta ogni mese" : "Segna come ricorrente"} className={`p-1 ${r.ricorrente ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`} data-testid={`prev-spesa-star-${i}`}><Star size={16} className={r.ricorrente ? "fill-amber-400" : ""} /></button>
                    <button onClick={() => del("spese", i)} className="text-muted-foreground hover:text-rose-600 p-1" data-testid={`prev-spesa-del-${i}`}><Trash2 size={16} /></button>
                  </>
                ) : <span className="w-[26px]" />}
              </div>
            ))}
          </div>
          <TotalRow label="Totale spese" value={totSpese} color="text-rose-700" testid="tot-spese" />
        </div>
      </div>
      )}

      {!loading && (
      <>
        {/* UTILE OPERATIVO */}
        <div className="gr-card p-5 mt-5 border-l-4" style={{ borderColor: utile >= 0 ? "#1f7a5c" : "#be123c" }} data-testid="prev-utile-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 font-head font-bold text-lg"><Wallet size={20} className="text-primary" /> Utile operativo del mese</div>
            <div className={`text-3xl font-head font-bold tabnum ${utile >= 0 ? "text-primary" : "text-rose-600"}`} data-testid="prev-utile-value">{fmtEur(utile)}</div>
          </div>
          <div className="text-xs text-muted-foreground mt-1">Ricavi {fmtEur(totRicavi)} − Spese {fmtEur(totSpese)}</div>
        </div>

        {/* EXTRA */}
        <div className="mt-5 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-muted-foreground"><Sparkles size={15} /> Sezione extra (altri ricavi / spese)</div>
        <div className="grid lg:grid-cols-2 gap-5 mt-2">
          <div className="gr-card p-5" data-testid="prev-ricavi-extra-card">
            <div className="flex items-center justify-between mb-3">
              <div className="font-head font-bold flex items-center gap-2 text-emerald-700"><TrendingUp size={18} /> Ricavi extra</div>
              <Button size="sm" variant="outline" onClick={() => add("ricavi_extra", { desc: "", importo: "" })} data-testid="prev-add-ricavo-extra"><Plus size={14} className="mr-1" /> Riga</Button>
            </div>
            <RowsEditor list={sheet.ricavi_extra} listKey="ricavi_extra" upd={upd} del={del} placeholder="Altro ricavo (es. Rimborso, Donazione)" testid="ricavo-extra" />
            <TotalRow label="Totale ricavi extra" value={totRicaviExtra} color="text-emerald-700" testid="tot-ricavi-extra" />
          </div>
          <div className="gr-card p-5" data-testid="prev-spese-extra-card">
            <div className="flex items-center justify-between mb-3">
              <div className="font-head font-bold flex items-center gap-2 text-rose-700"><TrendingDown size={18} /> Spese extra</div>
              <Button size="sm" variant="outline" onClick={() => add("spese_extra", { desc: "", importo: "" })} data-testid="prev-add-spesa-extra"><Plus size={14} className="mr-1" /> Riga</Button>
            </div>
            <RowsEditor list={sheet.spese_extra} listKey="spese_extra" upd={upd} del={del} placeholder="Altra spesa (es. Straordinaria)" testid="spesa-extra" />
            <TotalRow label="Totale spese extra" value={totSpeseExtra} color="text-rose-700" testid="tot-spese-extra" />
          </div>
        </div>

        <div className="gr-card p-4 mt-3 border-l-4" style={{ borderColor: "#c2660c" }} data-testid="prev-extra-result-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-head font-bold flex items-center gap-2"><Sparkles size={17} className="text-amber-600" /> Risultato extra</div>
            <div className={`text-2xl font-head font-bold tabnum ${risultatoExtra >= 0 ? "text-amber-700" : "text-rose-600"}`} data-testid="prev-extra-result-value">{fmtEur(risultatoExtra)}</div>
          </div>
        </div>

        {/* RISULTATO FINALE */}
        <div className="gr-card p-6 mt-5 bg-primary text-white" data-testid="prev-finale-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-head font-bold text-lg">Risultato finale complessivo</div>
              <div className="text-xs opacity-80 mt-0.5">Utile operativo {fmtEur(utile)} + Risultato extra {fmtEur(risultatoExtra)}</div>
            </div>
            <div className="text-4xl font-head font-bold tabnum" data-testid="prev-finale-value">{fmtEur(risultatoFinale)}</div>
          </div>
        </div>
      </>
      )}
    </div>
  );
}

function RowsEditor({ list, listKey, upd, del, placeholder, testid }) {
  if (!list.length) return <div className="text-sm text-muted-foreground italic py-3" data-testid={`prev-${testid}-empty`}>Nessuna voce. Aggiungi una riga.</div>;
  return (
    <div className="space-y-2">
      {list.map((r, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`prev-${testid}-${i}`}>
          <Input value={r.desc || ""} onChange={(e) => upd(listKey, i, "desc", e.target.value)} placeholder={placeholder} className="flex-1" data-testid={`prev-${testid}-desc-${i}`} />
          <Input type="text" inputMode="decimal" value={r.importo} onChange={(e) => upd(listKey, i, "importo", e.target.value.replace(/[^\d.,]/g, "").replace(",", "."))} placeholder="0" className="w-32 text-right tabnum" data-testid={`prev-${testid}-importo-${i}`} />
          <button onClick={() => upd(listKey, i, "ricorrente", !r.ricorrente)} title={r.ricorrente ? "Ricorrente: verrà riproposta ogni mese" : "Segna come ricorrente"} className={`p-1 ${r.ricorrente ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`} data-testid={`prev-${testid}-star-${i}`}><Star size={16} className={r.ricorrente ? "fill-amber-400" : ""} /></button>
          <button onClick={() => del(listKey, i)} className="text-muted-foreground hover:text-rose-600 p-1" data-testid={`prev-${testid}-del-${i}`}><Trash2 size={16} /></button>
        </div>
      ))}
    </div>
  );
}

function TotalRow({ label, value, color, testid }) {
  return (
    <div className="flex items-center justify-between mt-3 pt-3 border-t">
      <span className="text-sm font-bold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={`text-lg font-head font-bold tabnum ${color}`} data-testid={`prev-${testid}`}>{fmtEur(value)}</span>
    </div>
  );
}
